import { useEffect, useMemo, useState } from "react";
import {
  summarizeSeason,
  buildRadar,
  buildCapabilities,
  buildInvolvementMap,
  resolveSeasonFromQuery,
  buildRadarInsight,
  buildTransferInsight,
  buildInvolvementInsight,
  buildCapabilityInsight,
} from "../../lib/playerReport";
import ReportRadarChart from "../intel/report/ReportRadarChart";
import ReportTransferChart from "../intel/report/ReportTransferChart";
import ReportInvolvementPitch from "../intel/report/ReportInvolvementPitch";
import { CAPABILITY_CHARTS } from "../intel/report/ReportCapabilityCards";
import { resolveChartKeys } from "../../lib/playerCharts";

const EP = (id) => `https://stats.interaios.com/api/v1/players-sportsmonk/get-player/${id}`;
const playerCache = new Map();

const COMPARE_RE = /\b(compare|comparison|versus|vs|both)\b|side by side|two players/i;

/* One player's chart for one chart key — owns the fetch, report computation and
   the (static) heading/summary so several players can be stacked in a comparison. */
function PlayerChartBlock({ player, chartKey, query, showName }) {
  const playerId = player.id;
  const [data, setData] = useState(() => playerCache.get(playerId) || null);
  const [loading, setLoading] = useState(!playerCache.has(playerId));
  const [error, setError] = useState(null);

  useEffect(() => {
    const cached = playerCache.get(playerId);
    if (cached) {
      setLoading(false);
      setError(null);
      setData(cached);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    setData(null);
    (async () => {
      try {
        const res = await fetch(EP(playerId));
        if (!res.ok) throw new Error(`Player data unavailable (${res.status})`);
        const json = await res.json();
        if (!json?.data?.profile) throw new Error("Player data unavailable");
        playerCache.set(playerId, json.data);
        if (!cancelled) setData(json.data);
      } catch (e) {
        if (!cancelled) setError(e.message || "Failed to load player");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [playerId]);

  const profile = data?.profile;
  const rawStats = useMemo(() => data?.profile?.statistics || data?.statistics || [], [data]);
  const entry = useMemo(() => resolveSeasonFromQuery(rawStats, query), [rawStats, query]);

  const positionCode = profile?.detailedposition?.code || profile?.position?.code || "";
  const roleLabel = profile?.detailedposition?.name || profile?.position?.name || "";
  const summary = useMemo(() => (entry ? summarizeSeason(entry) : null), [entry]);

  const radarAxes = useMemo(() => {
    if (!entry || !summary) return [];
    const hasAerialData = (entry.details || []).some(
      (detail) => detail.type?.code === "aeriels-won" || detail.type?.code === "aerials-won",
    );
    const aerialWon = (entry.details || []).find(
      (detail) => detail.type?.code === "aeriels-won" || detail.type?.code === "aerials-won",
    )?.value?.total ?? 0;
    return buildRadar({ ...summary, aerialsWon: aerialWon }).filter((axis) => axis.key !== "aerial" || hasAerialData);
  }, [entry, summary]);

  const capabilities = useMemo(
    () => (entry && summary ? buildCapabilities(entry, summary) : null),
    [entry, summary],
  );

  const involvement = useMemo(() => {
    if (!entry || !summary) return null;
    const lineups = data?.profile?.lineups || data?.lineups || [];
    const seasonLineups = lineups.filter((lineup) => lineup.fixture?.season_id === entry.season?.id);
    return buildInvolvementMap(seasonLineups, positionCode, summary);
  }, [data, entry, summary, positionCode]);

  const transfers = useMemo(() => data?.transfers || data?.profile?.transfers || [], [data]);

  const radarInsight = useMemo(() => buildRadarInsight(radarAxes), [radarAxes]);
  const transferInsight = useMemo(() => buildTransferInsight(transfers), [transfers]);
  const involvementInsight = useMemo(
    () => buildInvolvementInsight(involvement, roleLabel),
    [involvement, roleLabel],
  );

  const ai = null;

  const slide = useMemo(() => {
    if (chartKey === "radar") {
      if (!radarAxes.length) return null;
      return {
        label: "Player Capability Radar",
        heading: ai?.radar?.heading,
        insight: ai?.radar?.insight || radarInsight,
        chart: <ReportRadarChart axes={radarAxes} />,
      };
    }
    if (chartKey === "transfer") {
      return {
        label: "Transfer Value",
        heading: ai?.transfer?.heading,
        insight: ai?.transfer?.insight || transferInsight,
        chart: <ReportTransferChart transfers={transfers} hideSummary />,
      };
    }
    if (chartKey === "involvementMap") {
      if (!involvement) return null;
      return {
        label: "Formation Involvement",
        heading: ai?.involvement?.heading,
        insight: ai?.involvement?.insight || involvementInsight,
        chart: <ReportInvolvementPitch involvement={involvement} roleLabel={roleLabel} />,
      };
    }
    const definition = CAPABILITY_CHARTS.find((chart) => chart.key === chartKey);
    const capData = capabilities?.[chartKey];
    if (definition && capData) {
      const capAi = ai?.capabilities?.[chartKey];
      return {
        label: definition.label,
        heading: capAi?.heading,
        insight: capAi?.insight || buildCapabilityInsight(chartKey, capData),
        chart: <definition.Chart data={capData} />,
      };
    }
    return null;
  }, [
    chartKey, radarAxes, transfers, involvement, capabilities, ai,
    radarInsight, transferInsight, involvementInsight, roleLabel,
  ]);

  if (loading) return <div className="charts-state">Loading {player.name || "player"}…</div>;
  if (error) return <div className="charts-state charts-state--error">{error}</div>;
  if (!slide) return <div className="charts-state">No chart available for this query.</div>;

  return (
    <div className="charts-block">
      {(showName || summary?.seasonName) && (
        <div className="charts-block-head">
          {showName && <span className="charts-block-name">{player.name || `#${playerId}`}</span>}
          {summary?.seasonName && <span className="charts-season">{summary.seasonName}</span>}
        </div>
      )}
      <div className="charts-chart">{slide.chart}</div>
      <div className="charts-text">
        <p className="charts-heading">{slide.heading || slide.label}</p>
        {slide.insight && <p className="charts-insight">{slide.insight}</p>}
      </div>
    </div>
  );
}

export default function PlayerChartsPanel({ players = [], activeId, query, responseText, onSelect, onClose }) {
  const [slide, setSlide] = useState(0);

  const comparison = players.length > 1 && COMPARE_RE.test(String(query || ""));
  const visiblePlayers = comparison
    ? players
    : [players.find((player) => player.id === activeId) || players[0]].filter(Boolean);

  const chartKeys = useMemo(() => resolveChartKeys(responseText, { query, players }), [responseText, query, players]);
  const chartKeyString = chartKeys.join("|");

  useEffect(() => { setSlide(0); }, [chartKeyString, comparison]);

  const count = chartKeys.length;
  const index = Math.min(slide, Math.max(0, count - 1));
  const activeKey = chartKeys[index] || null;

  return (
    <aside className={`charts-panel${comparison ? " is-compare" : ""}`} aria-label="Player charts">
      <header className="charts-head">
        <span className="charts-eyebrow">Player charts</span>
        {players.length > 1 && !comparison ? (
          <select
            className="charts-select"
            value={visiblePlayers[0]?.id || ""}
            onChange={(event) => onSelect?.(event.target.value)}
            aria-label="Choose player"
          >
            {players.map((player) => (
              <option key={player.id} value={player.id}>{player.name || `#${player.id}`}</option>
            ))}
          </select>
        ) : (
          <span className="charts-player">
            {comparison
              ? players.map((player) => player.name || `#${player.id}`).join("  vs  ")
              : visiblePlayers[0]?.name || ""}
          </span>
        )}
        <button className="charts-close" onClick={onClose} aria-label="Hide charts">×</button>
      </header>

      <div className="charts-body">
        {activeKey ? (
          <>
            {visiblePlayers.map((player) => (
              <PlayerChartBlock
                key={player.id}
                player={player}
                chartKey={activeKey}
                query={query}
                showName={comparison}
              />
            ))}
            {count > 1 && (
              <div className="charts-nav">
                <button
                  type="button"
                  onClick={() => setSlide((current) => Math.max(0, current - 1))}
                  disabled={index === 0}
                  aria-label="Previous chart"
                >
                  ‹
                </button>
                <span className="charts-count">{index + 1} / {count}</span>
                <button
                  type="button"
                  onClick={() => setSlide((current) => Math.min(count - 1, current + 1))}
                  disabled={index === count - 1}
                  aria-label="Next chart"
                >
                  ›
                </button>
              </div>
            )}
          </>
        ) : (
          <div className="charts-state">No charts listed for this response.</div>
        )}
      </div>
    </aside>
  );
}
